import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker as MuiDatepicker } from "@mui/x-date-pickers/DatePicker";
import { FormHelperText, SxProps } from "@mui/material";
import { useCountrySetting } from "@/hooks/useSetting";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/en-gb";

interface BasicDatepickerProps {
  value?: dayjs.Dayjs | null;
  label: string;
  onChange?: (value: dayjs.Dayjs | null) => void;
  sx?: SxProps<object>;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
  disabled?: boolean;
  disableWrite?: boolean;
  disableFuture?: boolean;
  format?: string;
  errorMessage?: string;
  /**
   * Adds a "Clear" button to the calendar (it also works when the field is
   * read-only); clearing calls `onChange(null)`.
   */
  clearable?: boolean;
}

type LocaleKey = "en" | "en-gb";

const Datepicker: React.FC<BasicDatepickerProps> = ({
  value,
  label,
  onChange,
  sx,
  error,
  helperText,
  required = false,
  minDate,
  maxDate,
  disabled = false,
  disableWrite = false,
  disableFuture = true,
  format,
  errorMessage,
  clearable = false,
}) => {
  const [locale, setLocale] = React.useState<LocaleKey>("en");

  const countrySetting = useCountrySetting();

  useEffect(() => {
    if (countrySetting === "US") {
      setLocale("en");
    } else if (countrySetting === "UK") {
      setLocale("en-gb");
    }
  }, [countrySetting]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
      <Box
        sx={{
          minWidth: 180,
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          maxWidth: "100%",
          ...sx,
        }}
      >
        <FormControl fullWidth error={error}>
          <MuiDatepicker
            value={value}
            label={label}
            disableFuture={disableFuture}
            minDate={minDate}
            maxDate={maxDate}
            onChange={onChange}
            slotProps={{
              textField: {
                error: error,
                helperText: error ? helperText : "",
                required: required,
              },
              field: {
                readOnly: disableWrite,
              },
              ...(clearable ? { actionBar: { actions: ["clear"] } } : {}),
            }}
            disabled={disabled}
            format={format}
          />
        </FormControl>
        {errorMessage && (
          <FormHelperText className="px-3.5 pt-0.5 !text-error-main">
            {errorMessage}
          </FormHelperText>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default Datepicker;
